import { useState } from "react";
import { AiOutlineDelete } from "react-icons/ai";
import { MdStar } from "react-icons/md";
import LikeButton from "@/components/LikeButton";
import type { CartItem } from "@/context/CartContext";
import { useCart } from "@/context/CartContext";
import ButtonPrimary from "@/shared/Button/ButtonPrimary";
import Input from "@/shared/Input/Input";
import InputNumber from "@/shared/InputNumber/InputNumber";
import ContactInfo from "./ContactInfo";
import PaymentMethod from "./PaymentMethod";
import ShippingAddress from "./ShippingAddress";
import { Link } from "react-router-dom";
import {
  OrderData,
  CreateOrderRequest,
  CreateOrderResponse,
} from "./order_models";

const CheckoutPage = () => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<number>(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [tabActive, setTabActive] = useState<
    "ContactInfo" | "ShippingAddress" | "PaymentMethod"
  >("ShippingAddress");

  const { cart } = useCart();

  const handleScrollToEl = (id: string) => {
    const element = document.getElementById(id);
    setTimeout(() => {
      element?.scrollIntoView({ behavior: "smooth" });
    }, 80);
  };

  const handlePaymentMethodChange = (paymentMethod: number) => {
    setSelectedPaymentMethod(paymentMethod);
  };

  const processCheckout = () => {
    if (selectedPaymentMethod === 1) {
      handleHostedCheckout();
    } else if (selectedPaymentMethod === 2) {
      // handlePayrollSDK();
    } else if (selectedPaymentMethod === 3) {
      // handle paypal
    } else if (selectedPaymentMethod === 4) {
      // handle card
    } else if (selectedPaymentMethod === 5) {
    }
  };

  // const handleSdkCheckout = async () => {
  //   await window.MyCheckoutSDK.checkout({
  //     merchantLocationId: "3da6db9a-f977-4b76-bf9b-4f5046f89718",
  //     merchantOrderId: new Date().toString(),
  //     title: "Store Title",
  //     description: "Order #112s3",
  //     amount: 1,
  //     currency: "GBP",
  //     email: "tech@relm.co",
  //     screening: true,
  //     acceptUrl: "https://xkicks.store/success",
  //     cancelUrl: "https://xkicks.store/failure",
  //     callbackUrl: "https://xkicks.store/success",
  //   });
  // };

  const handleHostedCheckout = async (): Promise<OrderData> => {
    try {
      setIsProcessing(true);
      const orderID = `e-${Math.floor(Math.random() * 1000000)}`;

      const keyID = window.__RUNTIME_CONFIG__?.REACT_APP_KEY_ID;
      const privateKey = window.__RUNTIME_CONFIG__?.REACT_APP_PRIVATE_KEY;
      const publicKey = window.__RUNTIME_CONFIG__?.REACT_APP_PUBLIC_KEY;
      const baseUrl = window.__RUNTIME_CONFIG__?.REACT_APP_BASE_URL;
      const selfUrl = window.__RUNTIME_CONFIG__?.REACT_APP_SELF_URL;
      const orderAmount = window.__RUNTIME_CONFIG__?.REACT_APP_ORDER_AMOUNT;
      const orderCurrency = window.__RUNTIME_CONFIG__?.REACT_APP_ORDER_CURRENCY;

      let payload: CreateOrderRequest = {
        clientOrderId: orderID,
        orderAmount: orderAmount ?? "100",
        currencyCode: orderCurrency ?? "USD",
        recipientEmail: "engineering@xbdgroup.com",
        redirectUrl: {
          accept: selfUrl + "/success",
          cancel: selfUrl + "/cancel",
          failed: selfUrl + "/failure",
        },
      };

      const stringifiedPayload = JSON.stringify(payload);
      const path = "/v1/pos/client/order/create";

      const signRes = await fetch(`${baseUrl}/v1/api-keys/${keyID}/signature`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          privateKey,
          publicKey,
          method: "POST",
          path,
          payload: stringifiedPayload,
          contentType: "application/json",
        }),
      });

      if (!signRes.ok)
        throw new Error(`Signature generation failed: ${signRes.status}`);

      const signResult = await signRes.json();
      const signature = signResult?.data?.signature;
      if (!signature)
        throw new Error("No signature returned from signing service");

      const orderRes = await fetch(`${baseUrl}${path}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: signature,
        },
        body: JSON.stringify(payload),
      });

      if (!orderRes.ok)
        throw new Error(`Order create failed: ${orderRes.status}`);

      const result: CreateOrderResponse = await orderRes.json();
      const orderData = result.data;

      if (orderData.paymentLink) {
        window.location.href = orderData.paymentLink;
      }

      return orderData;
    } catch (error) {
      console.error("Hosted checkout error:", error);
      setIsProcessing(false);
      throw error;
    }
  };

  const renderProduct = (item: CartItem) => {
    const { name, coverImage, price, slug, rating, shoeCategory } = item;
    return (
      <div key={name} className="flex py-5 last:pb-0">
        <div className="relative size-24 shrink-0 overflow-hidden rounded-xl md:size-40">
          <img
            src={coverImage}
            alt={name}
            className="size-full object-contain object-center"
          />
          <Link className="absolute inset-0" to={`/products/${slug}`} />
        </div>
        <div className="ml-4 flex flex-1 flex-col justify-between">
          <div className="flex justify-between">
            <div>
              <h3 className="font-medium md:text-2xl">
                <Link to={`/products/${slug}`}>{name}</Link>
              </h3>
              <span className="my-1 text-sm text-neutral-500">
                {shoeCategory}
              </span>
              <div className="flex items-center gap-1">
                <MdStar className="text-yellow-400" />
                <span className="text-sm">{rating}</span>
              </div>
            </div>
            <span className="font-medium md:text-xl">${price}</span>
          </div>
          <div className="flex w-full items-end justify-between text-sm">
            <div className="flex items-center gap-3">
              <LikeButton />
              <AiOutlineDelete className="text-2xl" />
            </div>
            <InputNumber />
          </div>
        </div>
      </div>
    );
  };

  const renderLeft = () => (
    <div className="space-y-8">
      <div id="ContactInfo" className="scroll-mt-24">
        <ContactInfo
          isActive={tabActive === "ContactInfo"}
          onOpenActive={() => {
            setTabActive("ContactInfo");
            handleScrollToEl("ContactInfo");
          }}
          onCloseActive={() => {
            setTabActive("ShippingAddress");
            handleScrollToEl("ShippingAddress");
          }}
        />
      </div>
      <div id="ShippingAddress" className="scroll-mt-24">
        <ShippingAddress
          isActive={tabActive === "ShippingAddress"}
          onOpenActive={() => {
            setTabActive("ShippingAddress");
            handleScrollToEl("ShippingAddress");
          }}
          onCloseActive={() => {
            setTabActive("PaymentMethod");
            handleScrollToEl("PaymentMethod");
          }}
        />
      </div>
      <div id="PaymentMethod" className="scroll-mt-24">
        <PaymentMethod
          isActive={tabActive === "PaymentMethod"}
          onOpenActive={() => {
            setTabActive("PaymentMethod");
            handleScrollToEl("PaymentMethod");
          }}
          onCloseActive={() => setTabActive("PaymentMethod")}
          onPaymentMethodChange={handlePaymentMethodChange}
        />
      </div>
    </div>
  );

  return (
    <div className="nc-CheckoutPage">
      <main className="container py-16 lg:pb-28 lg:pt-20">
        <div className="mb-16">
          <h2 className="block text-2xl font-semibold sm:text-3xl lg:text-4xl">
            Checkout
          </h2>
        </div>
        <div className="flex flex-col lg:flex-row">
          <div className="flex-1">{renderLeft()}</div>
          <div className="my-10 shrink-0 border-t border-neutral-300 lg:mx-10 lg:my-0 lg:border-l lg:border-t-0 xl:lg:mx-14 2xl:mx-16" />
          <div className="w-full lg:w-[36%]">
            <h3 className="text-lg font-semibold">Order summary</h3>
            <div className="mt-8 divide-y divide-neutral-300">
              {cart.slice(0, 3).map((item) => renderProduct(item))}
            </div>
            <div className="mt-10 border-t border-neutral-300 pt-6 text-sm">
              <div>
                <div className="text-sm">Discount code</div>
                <div className="mt-1.5 flex">
                  <Input
                    rounded="rounded-lg"
                    sizeClass="h-12 px-4 py-3"
                    className="flex-1 border-neutral-300 bg-transparent placeholder:text-neutral-500 focus:border-primary"
                  />
                  <button
                    type="button"
                    className="ml-3 flex w-24 items-center justify-center rounded-2xl border border-neutral-300 bg-gray px-4 text-sm font-medium transition-colors hover:bg-neutral-100"
                  >
                    Apply
                  </button>
                </div>
              </div>
              <div className="mt-4 flex justify-between pb-4">
                <span>Subtotal</span>
                <span className="font-semibold">$249.00</span>
              </div>
              <div className="flex justify-between py-4">
                <span>Estimated Delivery & Handling</span>
                <span className="font-semibold">Free</span>
              </div>
              <div className="flex justify-between py-4">
                <span>Estimated taxes</span>
                <span className="font-semibold">$24.90</span>
              </div>
              <div className="flex justify-between pt-4 text-base font-semibold">
                <span>Total</span>
                <span>$276.00</span>
              </div>
            </div>
            <ButtonPrimary
              className="mt-8 w-full"
              loading={isProcessing}
              disabled={isProcessing}
              onClick={processCheckout}
            >
              Confirm order
            </ButtonPrimary>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CheckoutPage;
